import React from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const AboutValues: React.FC = () => {
  const { elementRef: ref1, isVisible: isVisible1 } = useScrollAnimation({ triggerOnce: true });
  const { elementRef: ref2, isVisible: isVisible2 } = useScrollAnimation({ triggerOnce: true });
  const { elementRef: ref3, isVisible: isVisible3 } = useScrollAnimation({ triggerOnce: true });

  const values = [
    {
      title: "Missão",
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      description: "Transformar vidas através da estética de excelência, promovendo autoestima e bem-estar com tratamentos personalizados que respeitam a individualidade de cada cliente.",
      gradient: "from-amber-400 to-orange-500"
    },
    {
      title: "Visão",
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      ),
      description: "Ser referência nacional em harmonização facial e tratamentos estéticos, reconhecida pela excelência técnica, resultados naturais e atendimento humanizado.",
      gradient: "from-secondary to-primary"
    },
    {
      title: "Valores",
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      ),
      description: "Ética, transparência, excelência técnica, inovação constante e respeito à naturalidade. Valorizamos cada cliente como único e merecedor de cuidado personalizado.",
      gradient: "from-rose-400 to-pink-500"
    }
  ];

  return (
    <section className="py-20 md:py-32 bg-white overflow-hidden relative">
      {/* Background Decorations */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-secondary/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>

      <div className="container-custom relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 md:mb-20">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-gray-800 mb-6">
            Nossos <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-primary">Princípios</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Os pilares que guiam nossa atuação e definem nossa identidade como referência em estética
          </p>
        </div>

        {/* Values Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {/* Missão */}
          <div
            ref={ref1 as React.RefObject<HTMLDivElement>}
            className={`group relative transition-all duration-1000 ${
              isVisible1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
          >
            <div className="relative h-full bg-gradient-to-br from-white to-gray-50 rounded-3xl p-8 lg:p-10 shadow-xl border border-gray-100 hover:shadow-2xl hover:scale-105 transition-all duration-500">
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-amber-400/10 to-orange-500/10 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              {/* Content */}
              <div className="relative z-10">
                {/* Icon */}
                <div className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br ${values[0].gradient} text-white mb-6 shadow-lg transform transition-transform duration-500 group-hover:scale-110 group-hover:rotate-6`}>
                  {values[0].icon}
                </div>

                {/* Title */}
                <h3 className="text-3xl font-serif font-bold text-gray-800 mb-4">
                  {values[0].title}
                </h3>

                {/* Divider */}
                <div className={`w-16 h-1 bg-gradient-to-r ${values[0].gradient} mb-6 group-hover:w-24 transition-all duration-500`}></div>

                {/* Description */}
                <p className="text-gray-600 leading-relaxed">
                  {values[0].description}
                </p>
              </div>

              {/* Corner Decoration */}
              <div className={`absolute top-4 right-4 w-16 h-16 bg-gradient-to-br ${values[0].gradient} opacity-5 rounded-full group-hover:scale-150 transition-transform duration-700`}></div>
            </div>
          </div>

          {/* Visão */}
          <div
            ref={ref2 as React.RefObject<HTMLDivElement>}
            className={`group relative transition-all duration-1000 ${
              isVisible2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
            style={{ transitionDelay: '200ms' }}
          >
            <div className="relative h-full bg-gradient-to-br from-white to-gray-50 rounded-3xl p-8 lg:p-10 shadow-xl border border-gray-100 hover:shadow-2xl hover:scale-105 transition-all duration-500">
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-secondary/10 to-primary/10 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              {/* Content */}
              <div className="relative z-10">
                {/* Icon */}
                <div className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br ${values[1].gradient} text-white mb-6 shadow-lg transform transition-transform duration-500 group-hover:scale-110 group-hover:rotate-6`}>
                  {values[1].icon}
                </div>

                {/* Title */}
                <h3 className="text-3xl font-serif font-bold text-gray-800 mb-4">
                  {values[1].title}
                </h3>

                {/* Divider */}
                <div className={`w-16 h-1 bg-gradient-to-r ${values[1].gradient} mb-6 group-hover:w-24 transition-all duration-500`}></div>

                {/* Description */}
                <p className="text-gray-600 leading-relaxed">
                  {values[1].description}
                </p>
              </div>

              {/* Corner Decoration */}
              <div className={`absolute top-4 right-4 w-16 h-16 bg-gradient-to-br ${values[1].gradient} opacity-5 rounded-full group-hover:scale-150 transition-transform duration-700`}></div>
            </div>
          </div>

          {/* Valores */}
          <div
            ref={ref3 as React.RefObject<HTMLDivElement>}
            className={`group relative transition-all duration-1000 ${
              isVisible3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
            style={{ transitionDelay: '400ms' }}
          >
            <div className="relative h-full bg-gradient-to-br from-white to-gray-50 rounded-3xl p-8 lg:p-10 shadow-xl border border-gray-100 hover:shadow-2xl hover:scale-105 transition-all duration-500">
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-rose-400/10 to-pink-500/10 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              {/* Content */}
              <div className="relative z-10">
                {/* Icon */}
                <div className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br ${values[2].gradient} text-white mb-6 shadow-lg transform transition-transform duration-500 group-hover:scale-110 group-hover:rotate-6`}>
                  {values[2].icon}
                </div>

                {/* Title */}
                <h3 className="text-3xl font-serif font-bold text-gray-800 mb-4">
                  {values[2].title}
                </h3>

                {/* Divider */}
                <div className={`w-16 h-1 bg-gradient-to-r ${values[2].gradient} mb-6 group-hover:w-24 transition-all duration-500`}></div>

                {/* Description */}
                <p className="text-gray-600 leading-relaxed">
                  {values[2].description}
                </p>
              </div>

              {/* Corner Decoration */}
              <div className={`absolute top-4 right-4 w-16 h-16 bg-gradient-to-br ${values[2].gradient} opacity-5 rounded-full group-hover:scale-150 transition-transform duration-700`}></div>
            </div>
          </div>
        </div>

        {/* Bottom Quote */}
        <div className="mt-20 text-center">
          <div className="inline-block relative">
            <div className="absolute inset-0 bg-gradient-to-r from-secondary/20 to-primary/20 blur-2xl"></div>
            <p className="relative text-2xl md:text-3xl font-serif italic text-gray-700 max-w-4xl px-8">
              "Nossos valores não são apenas palavras. São o compromisso diário que assumimos com cada cliente."
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutValues;
